import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useTenant } from '@/contexts/TenantContext';
import { getPasswordComplexityErrors, PASSWORD_HINT } from '@/utils/passwordValidation';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    const pwErrors = getPasswordComplexityErrors(formData.password);
    if (pwErrors.length > 0) {
      toast.error(`Password must contain ${pwErrors.join(', ')}.`);
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await api.post('/auth/register', {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      const user = response.data;
      
      // Transfer guest cart to user's DB cart
      await transferGuestCart();
      
      // Show verification token in development
      if (user.verification_token) {
        toast.success(
          `Account created! Check console for verification link.`,
          { duration: 5000 }
        );
        console.log(`Verification link: ${window.location.origin}${tenantPath('/verify-email')}?token=${user.verification_token}`);
      } else {
        toast.success(`Welcome, ${user.name}!`);
      }
      
      navigate(tenantPath('/dashboard'), { state: { user }, replace: true });
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const transferGuestCart = async () => {
    try {
      const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      if (guestCart.length === 0) return;
      
      for (const item of guestCart) {
        try {
          await api.post('/cart', {
            product_id: item.product_id,
            quantity: item.quantity || 1,
            configuration: item.configuration || {}
          });
        } catch (e) {
          console.warn('Failed to transfer guest cart item to account:', item.product_id, e);
        }
      }
      localStorage.removeItem('guestCart');
    } catch (e) {
      console.warn('Failed to transfer guest cart:', e);
    }
  };
  
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <img src="/logo.png" alt="Golfshop.fit" className="h-20 w-20 mx-auto mb-4 rounded-full object-cover" data-testid="register-title" />
          <p className="text-gray-600 text-lg">Create your account</p>
        </div>
        
        <div className="bg-gray-50 p-8 rounded-lg border border-[#F3F4F6]">
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-gray-600 text-sm mb-2 block">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
                placeholder="John Doe"
                data-testid="name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-gray-600 text-sm mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
                placeholder="you@example.com"
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-gray-600 text-sm mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
                placeholder="••••••••"
                data-testid="password-input"
              />
              <p className="text-gray-500 text-xs mt-1">{PASSWORD_HINT}</p>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="text-gray-600 text-sm mb-2 block">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full bg-gray-100 border-[#F3F4F6] text-gray-900"
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 px-8 bg-[#334155] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#475569] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-button"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          
          <p className="text-center text-gray-600 text-sm mt-6">
            Already have an account?{' '}
            <Link to={tenantPath('/login')} className="text-[#334155] hover:text-[#475569] font-medium transition-colors">
              Sign In
            </Link>
          </p>
          
          <p className="text-gray-600 text-xs text-center mt-4">
            By creating an account, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
